/* eslint-disable @typescript-eslint/ban-ts-ignore */
import React, { useState } from 'react';
import * as Yup from 'yup';
import styled from 'styled-components/macro';
import { Formik, Form, FieldArray } from 'formik';
import CategoryTable from './CategoryTable';
import {
  localization,
  SurveyQuestionEditorDefinition,
} from 'survey-creator-react';
import { ElementFactory, Question, Serializer } from 'survey-core';
import {
  SurveyQuestionElementBase,
  ReactQuestionFactory,
} from 'survey-react-ui';

export interface CategoryType {
  name: string;
  i18n: {
    en: string;
    de: string;
    fr: string;
    it: string;
  };
}

Yup.addMethod(
  Yup.array,
  'unique',
  function (message: string, property: 'name') {
    return this.test('unique', message, function (list) {
      const mapper = (x: any) => x[property];

      //@ts-ignore
      const set = [...new Set(list.map(mapper))];
      const isUnique = list.length === set.length;
      if (isUnique) {
        return true;
      }

      const idx = list.findIndex(
        (l: CategoryType, i: number) => mapper(l) !== set[i]
      );

      return this.createError({
        path: `${this.path}[${idx}][${property}]`,
        message: message,
      });
    });
  }
);

interface FormValues {
  categories: CategoryType[];
  subcategories: CategoryType[];
}

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.13);
`;
const Content = styled.div`
  display: flex;
  width: 60rem;
  max-height: 95vh;
  overflow: scroll;
  padding: 1rem;
  background-color: white;
  flex-direction: column;
`;

const Footer = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
`;

const validationSchema = Yup.object().shape({
  categories: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Required'),
        i18n: Yup.object().shape({
          en: Yup.string().required('Required'),
          de: Yup.string().required('Required'),
          fr: Yup.string().required('Required'),
          it: Yup.string().required('Required'),
        }),
      })
    )
    // @ts-ignore
    .unique('Duplicate', 'name'),
  subcategories: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Required'),
        i18n: Yup.object().shape({
          en: Yup.string().required('Required'),
          de: Yup.string().required('Required'),
          fr: Yup.string().required('Required'),
          it: Yup.string().required('Required'),
        }),
      })
    )
    // @ts-ignore
    .unique('Duplicate', 'name'),
});

const Select = ({ saveValue, value }: any) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [values, setValues] = useState(value);

  const saveValues = (formValues: any) => {
    saveValue(formValues);
    setValues(formValues);
    setIsEditorOpen(false);
  };

  return (
    <div>
      <button onClick={() => setIsEditorOpen(true)}>Edit Categories</button>

      {isEditorOpen && (
        <Wrapper>
          <Content>
            <Formik
              validationSchema={validationSchema}
              initialValues={
                values || {
                  categories: [],
                  subcategories: [],
                }
              }
              onSubmit={saveValues}
            >
              {({ handleSubmit, errors, touched, values }) => {
                return (
                  <Form onSubmit={handleSubmit}>
                    <FieldArray name="categories">
                      {({ remove, push }) => {
                        return (
                          <CategoryTable
                            name="categories"
                            values={values.categories}
                            push={push}
                            remove={remove}
                            errors={errors.categories}
                            touched={touched.categories}
                          />
                        );
                      }}
                    </FieldArray>
                    <FieldArray name="subcategories">
                      {({ remove, push }) => {
                        return (
                          <CategoryTable
                            name="subcategories"
                            values={values.subcategories}
                            push={push}
                            remove={remove}
                            errors={errors.subcategories}
                            touched={touched.subcategories}
                          />
                        );
                      }}
                    </FieldArray>
                    <Footer>
                      <button type="submit">Save</button>
                      <button onClick={() => setIsEditorOpen(false)}>
                        Cancel
                      </button>
                    </Footer>
                  </Form>
                );
              }}
            </Formik>
          </Content>
        </Wrapper>
      )}
    </div>
  );
};

// create model for new question type
export class QuestionReportCategoriesModel extends Question {
  getType() {
    return 'reportCategories';
  }
  get sentimentType() {
    return this.getPropertyValue('reportCategoriesType');
  }
  set sentimentType(val) {
    this.setPropertyValue('reportCategoriesType', val);
  }
}

// set display title for question type
localization.getLocale('').qt.reportCategories = 'Report Categories';

// this serializes the class into JSON
Serializer.addClass(
  'reportCategories',
  [
    {
      name: 'reportCategoriesType',
    },
  ],
  function () {
    return new QuestionReportCategoriesModel('');
  },
  'question'
);

SurveyQuestionEditorDefinition.definition.sentiment = {
  properties: [{ name: 'reportCategoriesType', tab: 'layout' }],
};

// register question with its model
export function registerReportCategories() {
  ElementFactory.Instance.registerElement(
    'reportCategories',
    (name: string) => {
      return new QuestionReportCategoriesModel(name);
    }
  );
}

export class SurveyQuestionReportCategories extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.state = { value: this.question.value };
  }
  get question() {
    return this.questionBase;
  }
  get value() {
    return this.question.value;
  }

  get type() {
    // @ts-ignore
    return this.question.reportCategoriesType;
  }
  handleReportCategoriesChange = (data: any) => {
    this.question.value = data;
    this.setState({ value: data });
  };

  // support readOnly and designMode
  get style() {
    return this.question.getPropertyValue('readOnly') ||
      this.question.isDesignMode
      ? {
          pointerEvents: 'none',
        }
      : undefined;
  }

  renderElement() {
    return (
      <Select
        saveValue={this.handleReportCategoriesChange}
        value={this.state.value}
      />
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  'reportCategories',
  (props: any) => {
    return React.createElement(SurveyQuestionReportCategories, props);
  }
);
